import { useState, useRef } from 'react';
import { useMediaQuery, useQueryLocation } from '../hooks';
import html2canvas from 'html2canvas';
import Badge from './Badge';

function BadgeCelebrationModal({ badges, onClose, currentStreak, completedDate }) {
    // Safety check
    if (!badges || !Array.isArray(badges) || badges.length === 0) {
        console.error('BadgeCelebrationModal: Invalid badges prop', badges);
        return null;
    }
    
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isGeneratingImage, setIsGeneratingImage] = useState(false);
    const badgeCardRef = useRef(null);
    const [, navigate] = useQueryLocation();
    
    // Reusable class names
    const buttonBaseClasses = 'tw-bg-gray-100 tw-border-0 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-text-black hover:tw-bg-gray-200';
    const absoluteCenteredClasses = 'tw-absolute tw-top-1/2 tw-transform tw--translate-y-1/2';
    const socialButtonClasses = 'tw-bg-transparent tw-border-0 tw-cursor-pointer hover:tw-opacity-70';
    
    const BADGE_ICONS = {
        '1st-workout-complete': '🎯',
        '10-workouts-badge': '💯',
        '50-workouts-badge': '⭐',
        '100-workouts-badge': '🌟',
        'perfect-month': '💪',
        'new-years-badge': '🎉',
        '4th-of-july-badge': '🎆',
        'halloween-badge': '🎃',
        'thanksgiving-badge': '🦃',
        'christmas-badge': '🎄',
    };

    const BADGE_DESCRIPTIONS = {
        '1st-workout-complete': 'You received this badge for completing your first workout!',
        '10-workouts-badge': 'You received this badge for completing 10 workouts!',
        '50-workouts-badge': 'You received this badge for completing 50 workouts!',
        '100-workouts-badge': 'You received this badge for completing 100 workouts!',
        'perfect-month': 'You received this badge for completing a Lexi J workout every day for an entire month!',
        'new-years-badge': 'You received this badge for working out on New Years Day!',
        '4th-of-july-badge': 'You received this badge for working out on the 4th of July!',
        'halloween-badge': 'You received this badge for working out on Halloween!',
        'thanksgiving-badge': 'You received this badge for working out on Thanksgiving!',
        'christmas-badge': 'You received this badge for working out on Christmas!',
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        } else if (completedDate) {
            // Store completed date in sessionStorage for Calendar page
            sessionStorage.setItem('completedWorkoutDate', completedDate);
            // Navigate to calendar to show success banner
            navigate('/calendar');
        } else {
            // Force a full page reload to get fresh data from Shopify
            window.location.href = '/?habit-tracker/calendar';
        }
    };

    // Navigation handlers
    const handlePrevious = () => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : badges.length - 1));
    };
    
    const handleNext = () => {
        setCurrentIndex((prev) => (prev < badges.length - 1 ? prev + 1 : 0));
    };
    
    // Current badge
    const badge = badges[currentIndex];
    const badgeImage = badge?.fields?.Image || badge?.fields?.image || badge?.image;
    const badgeIcon = BADGE_ICONS[badge.handle] || '🏅';
    const badgeDescription = badge.description || BADGE_DESCRIPTIONS[badge.handle] || 'Congratulations on earning this badge!';
    
    // Format today's date
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US', { 
        month: 'short', 
        year: 'numeric' 
    }).toUpperCase();
    
    // Share functionality
    const shareText = `I just earned the ${badge.name} badge! 🎉`;
    const shareUrl = window.location.origin + '/?habit-tracker/badges';
    
    const handleFacebookShare = async () => {
        // Try to generate and share the image using Web Share API (works on mobile)
        if (navigator.share && navigator.canShare) {
            try {
                const element = badgeCardRef.current;
                if (!element) {
                    throw new Error('Badge element not found');
                }
                
                // Hide background and banner for sharing
                const originalBackground = element.style.backgroundImage;
                const originalBackgroundColor = element.style.backgroundColor;
                const banner = element.querySelector('.badge-banner');
                
                element.style.backgroundImage = 'none';
                element.style.backgroundColor = 'transparent';
                if (banner) banner.style.display = 'none';
                
                // Capture the badge as an image at high quality
                const canvas = await html2canvas(element, {
                    scale: 6,
                    backgroundColor: null,
                    logging: false,
                    useCORS: true,
                    allowTaint: true
                });
                
                // Restore original styles
                element.style.backgroundImage = originalBackground;
                element.style.backgroundColor = originalBackgroundColor;
                if (banner) banner.style.display = '';
                
                // Convert to blob
                const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
                
                if (blob) {
                    const file = new File([blob], `${badge.name.replace(/\s+/g, '-')}-Badge.png`, { type: 'image/png' });
                    
                    // Check if we can share files
                    if (navigator.canShare({ files: [file] })) {
                        await navigator.share({
                            files: [file],
                            title: badge.name,
                            text: shareText
                        });
                        return;
                    }
                }
            } catch (err) {
                console.log('Web Share with image failed:', err);
            }
        }
        
        // Fallback to traditional Facebook share dialog
        const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
        window.open(url, '_blank', 'width=600,height=400');
    };
    
    const handleInstagramShare = async () => {
        // Try to generate and share the image using Web Share API
        if (navigator.share && navigator.canShare) {
            try {
                const element = badgeCardRef.current;
                if (!element) {
                    throw new Error('Badge element not found');
                }
                
                // Hide background and banner for sharing
                const originalBackground = element.style.backgroundImage;
                const originalBackgroundColor = element.style.backgroundColor;
                const banner = element.querySelector('.badge-banner');
                
                element.style.backgroundImage = 'none';
                element.style.backgroundColor = 'transparent';
                if (banner) banner.style.display = 'none';
                
                // Capture the badge as an image at high quality
                const canvas = await html2canvas(element, {
                    scale: 6,
                    backgroundColor: null,
                    logging: false,
                    useCORS: true,
                    allowTaint: true
                });
                
                // Restore original styles
                element.style.backgroundImage = originalBackground;
                element.style.backgroundColor = originalBackgroundColor;
                if (banner) banner.style.display = '';
                
                // Convert to blob
                const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
                
                if (blob) {
                    const file = new File([blob], `${badge.name.replace(/\s+/g, '-')}-Badge.png`, { type: 'image/png' });
                    
                    // Check if we can share files
                    if (navigator.canShare({ files: [file] })) {
                        await navigator.share({
                            files: [file],
                            title: badge.name,
                            text: shareText
                        });
                        return;
                    }
                }
            } catch (err) {
                // User cancelled or share failed - just log it, no fallback on mobile
                if (err.name !== 'AbortError') {
                    console.log('Share with image failed:', err);
                }
            }
        }
        
        // Desktop fallback only - open Instagram in new tab
        if (!isMobile) {
            window.open('https://www.instagram.com/', '_blank');
        }
    };
    
    const handleDownload = async () => {
        if (isGeneratingImage) return;
        
        setIsGeneratingImage(true);
        
        try {
            // Get the badge card element
            const element = badgeCardRef.current;
            if (!element) {
                throw new Error('Badge element not found');
            }
            
            // Hide background color and banner for download (transparent background)
            const originalBackgroundColor = element.style.backgroundColor;
            const banner = element.querySelector('.badge-banner');
            
            element.style.backgroundColor = 'transparent';
            if (banner) banner.style.display = 'none';
            
            // Capture the badge as an image at highest quality
            const canvas = await html2canvas(element, {
                scale: 6, // Highest quality - 6x resolution for crisp downloads
                backgroundColor: null, // Transparent background
                logging: false,
                useCORS: true,
                allowTaint: true,
                imageTimeout: 0,
                removeContainer: true
            });
            
            // Restore original styles
            element.style.backgroundColor = originalBackgroundColor;
            if (banner) banner.style.display = '';
            
            // Convert canvas to PNG blob
            const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
            
            if (!blob) {
                throw new Error('Failed to create image blob');
            }
            
            const fileName = `${badge.name.replace(/\s+/g, '-')}-Badge.png`;
            
            // On mobile, try Web Share API first (allows saving to Photos)
            // On desktop, skip share API and go straight to download
            if (isMobile && navigator.share && navigator.canShare) {
                try {
                    const file = new File([blob], fileName, { type: 'image/png' });
                    
                    // Check if we can share files
                    if (navigator.canShare({ files: [file] })) {
                        await navigator.share({
                            files: [file],
                            title: badge.name,
                            text: `${badge.name} Badge`
                        });
                        setIsGeneratingImage(false);
                        return;
                    }
                } catch (err) {
                    // User cancelled or share not supported, fall through to download
                    if (err.name === 'AbortError') {
                        setIsGeneratingImage(false);
                        return;
                    }
                    console.log('Web Share failed, falling back to download:', err);
                }
            }
            
            // Direct download (desktop default, mobile fallback)
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up
            URL.revokeObjectURL(url);
            setIsGeneratingImage(false);
            
        } catch (error) {
            console.error('PNG generation failed:', error);
            
            // Restore styles in case of error
            const element = badgeCardRef.current;
            if (element) {
                const banner = element.querySelector('.badge-banner');
                element.style.backgroundImage = '';
                if (banner) banner.style.display = '';
            }
            
            // Fallback: Try Web Share API (mobile only)
            if (isMobile && navigator.share) {
                try {
                    await navigator.share({
                        title: badge.name,
                        text: shareText,
                        url: shareUrl
                    });
                    setIsGeneratingImage(false);
                    return;
                } catch (err) {
                    if (err.name !== 'AbortError') {
                        console.log('Share failed:', err);
                    }
                }
            }
            
            // Final fallback: Copy to clipboard
            try {
                await navigator.clipboard.writeText(shareText + '\n' + shareUrl);
                alert('PNG generation failed. Badge text copied to clipboard! 📋');
            } catch (err) {
                alert('Unable to generate PNG or copy to clipboard.');
            }
            setIsGeneratingImage(false);
        }
    };

    return (
        <div className="tw-fixed tw-top-0 tw-left-0 tw-right-0 tw-bottom-0 tw-bg-black tw-bg-opacity-50 tw-z-[1002] tw-flex tw-items-center tw-justify-center tw-p-4">
            <div 
                className={`tw-relative tw-bg-white tw-rounded-[30px] tw-flex tw-flex-col tw-items-center tw-text-center tw-overflow-y-auto ${isMobile ? 'tw-max-h-[80vh]' : 'tw-max-h-[90vh]'}`}
                style={isMobile ? {
                    width: '377px',
                    padding: '20px'
                } : {
                    width: '800px',
                    padding: '50px 100px'
                }}
            >
                {/* Close button */}
                <button 
                    onClick={handleClose}
                    className={`tw-absolute ${buttonBaseClasses} ${isMobile ? 'tw-top-4 tw-right-4 tw-w-12 tw-h-12 tw-text-3xl' : 'tw-top-8 tw-right-8 tw-w-16 tw-h-16 tw-text-4xl'}`}
                    style={{ fontWeight: 'bold' }}
                >
                    ×
                </button>

                {/* Navigation buttons - only show if multiple badges */}
                {badges.length > 1 && (
                    <>
                        <button 
                            onClick={handlePrevious}
                            className={`${buttonBaseClasses} ${absoluteCenteredClasses} ${isMobile ? 'tw-left-2 tw-w-12 tw-h-12 tw-text-3xl' : 'tw-left-8 tw-w-16 tw-h-16 tw-text-4xl'}`}
                            style={{ fontWeight: 'bold' }}
                        >
                            ‹
                        </button>
                        <button 
                            onClick={handleNext}
                            className={`${buttonBaseClasses} ${absoluteCenteredClasses} ${isMobile ? 'tw-right-2 tw-w-12 tw-h-12 tw-text-3xl' : 'tw-right-8 tw-w-16 tw-h-16 tw-text-4xl'}`}
                            style={{ fontWeight: 'bold' }}
                        >
                            ›
                        </button>
                    </>
                )}

                {/* Badge counter */}
                {badges.length > 1 && (
                    <div 
                        className={`tw-absolute tw-top-4 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-text-gray-600 ${isMobile ? 'tw-text-sm' : 'tw-text-base'}`}
                        style={{
                            fontFamily: 'Gotham, sans-serif',
                            fontWeight: 400
                        }}
                    >
                        {currentIndex + 1} of {badges.length}
                    </div>
                )}

                {/* Title */}
                <h1 
                    className={`tw-m-0 ${isMobile ? 'tw-mb-6 tw-w-full tw-text-center' : 'tw-mb-16'}`}
                    style={isMobile ? {
                        color: '#000',
                        textAlign: 'center',
                        fontFamily: 'Anton',
                        fontSize: '30px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '110%',
                        textTransform: 'uppercase'
                    } : {
                        color: '#414042',
                        fontFamily: 'Anton',
                        fontSize: '32px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '115%',
                        textTransform: 'uppercase'
                    }}
                >
                    {badge.name.toUpperCase()}!
                </h1>
                
                {/* Badge Display - All badges with solid blue background */}
                <div 
                    ref={badgeCardRef}
                    className="tw-flex tw-flex-col tw-items-center tw-relative"
                    style={{
                        backgroundColor: '#568fb0',
                        width: isMobile ? '275px' : '400px',
                        height: isMobile ? '275px' : '400px',
                        borderRadius: '20px',
                        marginBottom: isMobile ? '20px' : '40px',
                        padding: '0'
                    }}
                >
                    {/* Top Banner with Streak and Logo - Only show for daily-streak-badge */}
                    {badge.handle === 'daily-streak-badge' && (
                        <div className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2 tw-bg-black tw-bg-opacity-20 badge-banner" style={{ borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}>
                            <img 
                                src="https://cdn.shopify.com/s/files/1/0667/9599/5332/files/Lexi_J_Wellness_Logo_Variations_Black_White-03.jpg?v=1732741440" 
                                alt="LJW Logo"
                                style={{ height: isMobile ? '20px' : '30px' }}
                            />
                            {currentStreak !== undefined && (
                                <div className="tw-flex tw-items-center tw-gap-1">
                                    <span className="tw-text-black tw-font-bold" style={{ fontFamily: 'Anton', fontSize: isMobile ? '18px' : '24px' }}>
                                        {currentStreak}
                                    </span>
                                    <span style={{ fontSize: isMobile ? '18px' : '24px' }}>🔥</span>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Badge content area */}
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-flex-1 tw-px-5 tw-pb-10">
                        {/* Badge image with optional streak number overlay - Hidden for download */}
                        <div className="tw-mb-6 tw-relative badge-image">
                            {badgeImage ? (
                                <img 
                                src={badgeImage} 
                                alt={badge.name}
                                style={{
                                    width: isMobile ? '120px' : '180px',
                                    height: isMobile ? '120px' : '180px',
                                    objectFit: 'contain'
                                }}
                            />
                        ) : (
                            <span style={{ fontSize: isMobile ? '120px' : '180px' }}>{badgeIcon}</span>
                        )}
                        {/* Overlay the streak number for daily-streak-badge */}
                        {badge.handle === 'daily-streak-badge' && badge.streakNumber && (
                            <div 
                                className="tw-absolute tw-bg-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-border-2 tw-border-black tw-font-bold"
                                style={{
                                    top: isMobile ? '13%' : '13%',
                                    right: isMobile ? '22%' : '22%',
                                    width: isMobile ? '24px' : '36px',
                                    height: isMobile ? '24px' : '36px',
                                    fontSize: isMobile ? '12px' : '18px'
                                }}
                            >
                                {badge.streakNumber}
                            </div>
                        )}
                        </div>
                        <p 
                            className={`tw-m-0 tw-text-center tw-font-bold badge-text tw-text-white`}
                            style={{
                                fontFamily: 'Anton',
                                fontSize: isMobile ? '20px' : '28px',
                                textTransform: 'uppercase',
                                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                                lineHeight: '110%'
                            }}
                        >
                            {badge.handle === 'daily-streak-badge' ? (
                                <>Consistency wins<br />with Lexi J Wellness</>
                            ) : (
                                <>I workout with<br />Lexi J Wellness</>
                            )}
                        </p>
                    </div>
                </div>

                {/* Description */}
                <p 
                    className={`tw-m-0 ${isMobile ? 'tw-mb-6 tw-max-w-full tw-w-full' : 'tw-mb-12 tw-max-w-[700px]'}`}
                    style={isMobile ? {
                        color: '#000',
                        textAlign: 'center',
                        fontFamily: 'Poppins',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '120%'
                    } : {
                        color: '#414042',
                        textAlign: 'center',
                        fontFamily: 'Poppins',
                        fontSize: '24px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '140%'
                    }}
                >
                    {badgeDescription}
                </p>
                
                {/* Share section */}
                <p 
                    className={`tw-m-0 ${isMobile ? 'tw-mb-4 tw-w-full' : 'tw-mb-8'}`}
                    style={isMobile ? {
                        color: '#000',
                        textAlign: 'center',
                        fontFamily: 'Poppins',
                        fontSize: '16px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '120%'
                    } : {
                        color: '#000',
                        textAlign: 'center',
                        fontFamily: 'Poppins',
                        fontSize: '24px',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        lineHeight: '140%'
                    }}
                >
                    Share your accomplishments!
                </p>
                
                {/* Social icons */}
                <div className={`tw-flex tw-items-center ${isMobile ? 'tw-gap-4 tw-w-full tw-justify-center' : 'tw-gap-8'}`}>
                    <button 
                        onClick={handleFacebookShare}
                        className={socialButtonClasses}
                        title="Share on Facebook"
                    >
                        <svg width={isMobile ? "18" : "26"} height={isMobile ? "18" : "26"} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                    </button>
                    <button 
                        onClick={handleInstagramShare}
                        className={socialButtonClasses}
                        title="Share on Instagram"
                    >
                        <svg width={isMobile ? "18" : "26"} height={isMobile ? "18" : "26"} viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                    </button>
                    {!isMobile && (
                    <button 
                        onClick={handleDownload}
                        className={socialButtonClasses}
                        title="Download as PNG"
                        disabled={isGeneratingImage}
                        style={{ opacity: isGeneratingImage ? 0.5 : 1 }}
                    >
                        {isGeneratingImage ? (
                            <svg width={isMobile ? "18" : "26"} height={isMobile ? "18" : "26"} viewBox="0 0 24 24" fill="currentColor">
                                <circle cx="12" cy="12" r="10" opacity="0.25"/>
                                <path d="M12 2a10 10 0 0 1 10 10" opacity="0.75">
                                    <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                                </path>
                            </svg>
                        ) : (
                            <svg width={isMobile ? "18" : "26"} height={isMobile ? "18" : "26"} viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                            </svg>
                        )}
                    </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default BadgeCelebrationModal;
